import { fabric } from "fabric"
import type Sign from "@/signomatic/app/sign"
import { sizeUnit } from "@/common/locales"
import Calc from "@/common/util/calculator"
import env from "@/common/env"
import i18n from "@/common/locales"
import ClientData from "@/common/client_data"
import { flagEnabled } from "./flags"

const thickness = (signs: SignType[], signType: string, signState: SignState, separator: string) => {
  let thickness: number | undefined
  const thicknessForLabelPlates = flagEnabled("thickness-for-label-plates")

  if (signType === "color_badge") {
    if (signState.material === "plastic") {
      thickness = 1.6
    } else if (signState.material === "aluminium") {
      thickness = 1
    } else if (signState.material === "wood") {
      thickness = 4
    } else if (signState.material === "ecoboard") {
      thickness = 2
    } else {
      switch (signState.shape) {
        case "bulge":
        case "ellipse":
          thickness = 0.51
          break
        case "round":
          thickness = 0.46
      }
    }
  } else if (signType === "brass_sign") {
    switch (signState.badgeName) {
      case "madrid":
      case "kiev":
      case "budapest":
        thickness = 0.51
        break
      case "london":
      case "stockholm":
        thickness = 5.1
        break
      case "oslo":
        thickness = 5
        break
      case "paris":
        thickness = 8
        break
      case "berlin":
      case "rome":
      case "sydney":
      case "lisbon":
      case "copenhagen":
      case "athens":
      case "amsterdam":
      case "tallinn":
        thickness = 2
        break
      case "prague":
        thickness = 1.5
        break
      case "dublin":
        thickness = 2.3
        break
    }
  } else if (signType === "name_sign") {
    thickness = signState.material === "wood" ? 4 : 1.6
  } else if (signType === "rollup") {
    thickness = signState.material === "eco" ? 0.33 : 0.22
  } else if (signType === "label_plates") {
    switch (signState.material) {
      case "regular":
        thickness = 1.6
        break
      case "brass":
      case "nickel":
        thickness = 0.51
        break
      case "stainless":
        thickness = 1
        break
      case "aluminium":
        if (!thicknessForLabelPlates) thickness = 1
        break
      case "aluminium_lacquered":
        if (!thicknessForLabelPlates) thickness = 1.5
        break
    }
  } else if (signType === "double_sided_sign") {
    thickness = signState.material === "ecoboard" ? 2 : 3
  } else if (signType === "sticker") {
    if (signState.color === "#f0f0f0") {
      thickness = 0.075
    } else if (signState.fixmethod === "self_adhesive_floor") {
      thickness = 0.205
    }
  }

  if (signState.thickness) {
    thickness = signState.thickness
  }

  if (!thickness) {
    const current = signs.find(sign => sign.key === signType)
    if (current) {
      return current.thickness
    }
  }

  if (thickness) {
    const microUnit = signType === "sticker" ? " (" + (thickness * 1000).toString() + "µ)" : ""

    if (sizeUnit === "in") {
      let decimals = 2
      if (signType === "sticker") {
        decimals = 4
      } else if (signType === "rollup") {
        decimals = 3
      }

      thickness = Calc.mmToIn(thickness, decimals)

      if (thickness === 0) {
        thickness = 0.1
      }
    }

    return thickness.toString().replace(".", separator) + " " + sizeUnit + microUnit
  }

  return null
}

const widthDimensionsGroup = (sign: Sign) => {
  let width = sizeUnit === "in" ? sign.state.widthIn : sign.state.width

  if (sign.state.forcedWidth) {
    width = Math.round(sizeUnit === "in" ? Calc.mmToIn(sign.state.forcedWidth) : sign.state.forcedWidth)
  }

  const widthMarginFromSign = (sign.signType === "rollup" ? 28 : 20) / sign.scale
  const thicknessMarginFromSign = (sign.signType === "rollup" ? 40 : 32) / sign.scale
  const signBottomX = sign.centerX - sign.state.widthPx / 2
  const signBottomY = sign.centerY + sign.state.heightPx / 2
  const separator = env.TEST ? "." : (i18n.t("locale.separator") as string)
  const thicknessValue = thickness(ClientData.signs || [], sign.signType || "", sign.state, separator)
  const widthText = new fabric.IText(`${width} ${sizeUnit}`, {
    fontSize: 12 / sign.scale,
    fontFamily: "Barlow",
  })
  const thicknessText = new fabric.IText(`${i18n.t("toolbox.thickness")}: ${thicknessValue}`, {
    fontSize: 12 / sign.scale,
    fontFamily: "Barlow",
  })
  const textPadding = 8 / sign.scale
  const lineEndLength = 9 / sign.scale

  const widthLeft = new fabric.Line(
    [
      signBottomX,
      signBottomY + widthMarginFromSign,
      signBottomX + (sign.state.widthPx / 2 - widthText.getBoundingRect().width / 2) - textPadding,
      signBottomY + widthMarginFromSign,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const widthRight = new fabric.Line(
    [
      signBottomX + (sign.state.widthPx / 2 + widthText.getBoundingRect().width / 2) + textPadding,
      signBottomY + widthMarginFromSign,
      signBottomX + sign.state.widthPx,
      signBottomY + widthMarginFromSign,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const widthLeftEnd = new fabric.Line(
    [
      signBottomX,
      signBottomY + widthMarginFromSign - lineEndLength / 2,
      signBottomX,
      signBottomY + widthMarginFromSign + lineEndLength / 2 + 0.5 / sign.scale,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const widthRightEnd = new fabric.Line(
    [
      signBottomX + sign.state.widthPx,
      signBottomY + widthMarginFromSign - lineEndLength / 2,
      signBottomX + sign.state.widthPx,
      signBottomY + widthMarginFromSign + lineEndLength / 2 + 0.5 / sign.scale,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const widthGuideText = new fabric.IText(`${width} ${sizeUnit}`, {
    name: "width",
    fontSize: 12 / sign.scale,
    left: signBottomX + sign.state.widthPx / 2 - widthText.getBoundingRect().width / 2,
    top: signBottomY + widthMarginFromSign - widthText.getBoundingRect().height / 2,
    fill: "#14213D",
    selectable: false,
    hoverCursor: "cursor",
    fontFamily: "Barlow",
    objectCaching: false,
    backgroundColor: "#f4f8fa",
  })
  const thicknessGuideText = new fabric.IText(`${i18n.t("products.thickness")}: ${thicknessValue}`, {
    name: "thickness",
    fontSize: 12 / sign.scale,
    left: signBottomX + sign.state.widthPx / 2 - thicknessText.getBoundingRect().width / 2,
    top: signBottomY + thicknessMarginFromSign,
    fill: "#74848D",
    selectable: false,
    hoverCursor: "cursor",
    fontFamily: "Barlow",
    objectCaching: false,
    backgroundColor: "#f4f8fa",
  })

  return new fabric.Group([widthLeft, widthRight, widthLeftEnd, widthRightEnd, widthGuideText, thicknessGuideText], {
    name: "width_dimensions",
    selectable: false,
    hoverCursor: "cursor",
    objectCaching: false,
  })
}

const heightDimensionsGroup = (sign: Sign) => {
  let height = sizeUnit === "in" ? sign.state.heightIn : sign.state.height

  if (sign.state.forcedHeight) {
    height = Math.round(sizeUnit === "in" ? Calc.mmToIn(sign.state.forcedHeight) : sign.state.forcedHeight)
  }

  const heightMarginFromSign = 20 / sign.scale
  const signRightX = sign.centerX + sign.state.widthPx / 2
  const signBottomY = sign.centerY + sign.state.heightPx / 2
  const heightText = new fabric.IText(`${height} ${sizeUnit}`, {
    fontSize: 12 / sign.scale,
    fontFamily: "Barlow",
  })

  const textPadding = 8 / sign.scale
  const lineEndLength = 9 / sign.scale

  const heightTop = new fabric.Line(
    [
      signRightX + heightMarginFromSign,
      signBottomY - sign.state.heightPx / 2 - heightText.getBoundingRect().width / 2 - textPadding,
      signRightX + heightMarginFromSign,
      signBottomY - sign.state.heightPx,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const heightBottom = new fabric.Line(
    [
      signRightX + heightMarginFromSign,
      signBottomY,
      signRightX + heightMarginFromSign,
      signBottomY - sign.state.heightPx / 2 + (heightText.getBoundingRect().width / 2 + textPadding),
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const heightBottomEnd = new fabric.Line(
    [
      signRightX + heightMarginFromSign - lineEndLength / 2,
      signBottomY,
      signRightX + heightMarginFromSign + lineEndLength / 2 + 0.5 / sign.scale,
      signBottomY,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const heightTopEnd = new fabric.Line(
    [
      signRightX + heightMarginFromSign - lineEndLength / 2,
      signBottomY - sign.state.heightPx,
      signRightX + heightMarginFromSign + lineEndLength / 2 + 0.5 / sign.scale,
      signBottomY - sign.state.heightPx,
    ],
    {
      strokeWidth: 1 / sign.scale,
      stroke: "#74848D",
      selectable: false,
      hoverCursor: "cursor",
      objectCaching: false,
    }
  )
  const heightGuideText = new fabric.IText(`${height} ${sizeUnit}`, {
    name: "height",
    fontSize: 12 / sign.scale,
    left: signRightX + heightMarginFromSign + heightText.getBoundingRect().height / 2,
    top: signBottomY - sign.state.heightPx / 2 - heightText.getBoundingRect().width / 2,
    angle: 90,
    fill: "#14213D",
    selectable: false,
    hoverCursor: "cursor",
    fontFamily: "Barlow",
    objectCaching: false,
    backgroundColor: "#f4f8fa",
  })

  return new fabric.Group([heightTop, heightBottom, heightTopEnd, heightBottomEnd, heightGuideText], {
    name: "height_dimensions",
    selectable: false,
    hoverCursor: "cursor",
    objectCaching: false,
  })
}

class SignDimensions {
  readonly sign: Sign

  constructor(sign: Sign) {
    this.sign = sign
  }

  redrawDimensions(sign: Sign) {
    const objects = sign.canvas.getObjects()
    const drawnWidthDimensions = objects.find((obj: fabric.Group) => obj.name === "width_dimensions")
    const drawnHeightDimensions = objects.find((obj: fabric.Group) => obj.name === "height_dimensions")

    if (drawnWidthDimensions) {
      sign.canvas.remove(drawnWidthDimensions)
    }

    if (drawnHeightDimensions) {
      sign.canvas.remove(drawnHeightDimensions)
    }

    sign.canvas.add(widthDimensionsGroup(sign))
    sign.canvas.add(heightDimensionsGroup(sign))
  }
}

export default SignDimensions
